// Public hook composing the auth-store + refresh mutation into the surface
// the rest of the app consumes — `{ user, token, isAuthed, refresh }` per the
// TASK-029 acceptance criteria.
//
// Selectors are individual primitive subscribers (the React 19-friendly
// idiom for Zustand 5) so callers re-render only when the field they read
// actually changes — equivalent to shallow equality without dragging in
// `zustand/shallow`.

import { useCallback } from "react";

import type { AuthUser } from "@community-analytics/shared";

import {
  selectAuthStatus,
  selectAuthToken,
  selectAuthUser,
  selectIsAuthed,
  useAuthStore,
  type AuthStatus,
} from "../model/auth-store";
import { postAuthRefresh } from "../api/useAuthMutations";

export interface UseAuthReturn {
  user: AuthUser | null;
  token: string | null;
  isAuthed: boolean;
  status: AuthStatus;
  /**
   * Re-validate the current Telegram.WebApp.initData and mint a fresh JWT.
   * Returns true if a new token is now in the store, false otherwise. The
   * fetch-client's 401-retry path calls this directly via the AuthBridge —
   * UI code typically shouldn't have to call it manually.
   */
  refresh: (initData: string) => Promise<boolean>;
}

export function useAuth(): UseAuthReturn {
  const user = useAuthStore(selectAuthUser);
  const token = useAuthStore(selectAuthToken);
  const isAuthed = useAuthStore(selectIsAuthed);
  const status = useAuthStore(selectAuthStatus);

  const refresh = useCallback(async (initData: string): Promise<boolean> => {
    const store = useAuthStore.getState();
    store.setAuthenticating();
    try {
      const response = await postAuthRefresh(initData);
      store.setAuthenticated({
        token: response.token,
        expiresAt: response.expiresAt,
        user: response.user,
      });
      return true;
    } catch (err) {
      store.setError(err instanceof Error ? err.message : "Refresh failed");
      return false;
    }
  }, []);

  return { user, token, isAuthed, status, refresh };
}
